import { EvoluaApiError } from "@evolua/api-client";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Field,
  FilterBar,
  Modal,
  PageHeader,
  SearchField,
  Select,
  Skeleton,
  Table,
  Textarea,
  badgeToneForStatus,
} from "@evolua/ui";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { QueryErrorState } from "../api/QueryErrorState.js";
import {
  useAssignHrRequest,
  useHrMetrics,
  useHrRequests,
  useReassignHrRequest,
  useRespondHrRequest,
} from "../api/use-hr.js";

/** Fila de solicitações do RH (FASE-06-T49). */
export function SolicitacoesPage() {
  const [type, setType] = useState<string>("");
  const [assignee, setAssignee] = useState<string>("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [q, setQ] = useState("");
  const [showClosed, setShowClosed] = useState(false);

  const filters = useMemo(
    () => ({
      ...(type ? { type } : {}),
      ...(assignee ? { assignee } : {}),
      ...(overdueOnly ? { overdue: true } : {}),
      ...(q.trim() ? { q: q.trim() } : {}),
      ...(showClosed ? { status: "APPROVED" as const } : {}),
    }),
    [type, assignee, overdueOnly, q, showClosed],
  );

  const query = useHrRequests(filters);
  const metrics = useHrMetrics();
  const assign = useAssignHrRequest();
  const reassign = useReassignHrRequest();
  const respond = useRespondHrRequest();

  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [respondId, setRespondId] = useState<string | null>(null);
  const [respondComment, setRespondComment] = useState("");
  const [respondDecision, setRespondDecision] = useState<"CLOSE" | "REJECT">("CLOSE");
  const [attachmentId, setAttachmentId] = useState("");
  const [reassignId, setReassignId] = useState<string | null>(null);
  const [reassignTo, setReassignTo] = useState("demo-hr-backup");
  const [reassignReason, setReassignReason] = useState("");

  function errorMessage(err: unknown): string {
    if (err instanceof EvoluaApiError) return err.problem.detail ?? err.message;
    if (err instanceof Error) return err.message;
    return "Falha na operação";
  }

  async function onAssign(id: string) {
    setActionError(null);
    setBusyId(id);
    try {
      await assign.mutateAsync({ id });
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  async function onReassignConfirm() {
    if (!reassignId || !reassignReason.trim()) return;
    setActionError(null);
    setBusyId(reassignId);
    try {
      await reassign.mutateAsync({
        id: reassignId,
        assigneeSubject: reassignTo.trim(),
        reason: reassignReason.trim(),
      });
      setReassignId(null);
      setReassignReason("");
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  async function onRespondConfirm() {
    if (!respondId || !respondComment.trim()) return;
    setActionError(null);
    setBusyId(respondId);
    try {
      await respond.mutateAsync({
        id: respondId,
        body: {
          comment: respondComment.trim(),
          decision: respondDecision,
          ...(attachmentId.trim() ? { attachmentObjectIds: [attachmentId.trim()] } : {}),
        },
      });
      setRespondId(null);
      setRespondComment("");
      setAttachmentId("");
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  }

  if (query.isPending) {
    return (
      <div className="evolua-stack evolua-stack--2" aria-busy="true">
        <Skeleton height="2rem" />
        <Skeleton height="8rem" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const items = query.data.data;
  const m = metrics.data;

  return (
    <>
      <PageHeader
        title="Solicitações"
        lead="Fila do RH com filtros, atribuição auditada, resposta com anexo e tempo de resposta. Notificações mock por etapa (entrega real = FASE 9)."
      />

      {m ? (
        <p className="evolua-page-lead">
          Abertas: {m.openCount} · Em atraso: {m.overdueCount} · Encerradas:{" "}
          {m.closedCount}
          {m.avgFirstResponseHours != null
            ? ` · 1ª resposta média: ${m.avgFirstResponseHours}h`
            : ""}
        </p>
      ) : null}

      {actionError ? (
        <Alert tone="danger" title="Não foi possível concluir">
          {actionError}
        </Alert>
      ) : null}

      <FilterBar
        onClear={() => {
          setType("");
          setAssignee("");
          setQ("");
          setOverdueOnly(false);
          setShowClosed(false);
        }}
      >
        <Select
          label="Tipo"
          value={type}
          onChange={(e) => setType(e.target.value)}
          options={[
            { value: "", label: "Todos" },
            { value: "CORRECTION_REQUEST", label: "Correção" },
            { value: "DECLARATION_REQUEST", label: "Declaração" },
            { value: "QUESTION_REQUEST", label: "Dúvida" },
            { value: "VACATION", label: "Férias" },
          ]}
        />
        <Select
          label="Responsável"
          value={assignee}
          onChange={(e) => setAssignee(e.target.value)}
          options={[
            { value: "", label: "Qualquer" },
            { value: "me", label: "Eu" },
            { value: "unassigned", label: "Fila compartilhada" },
            { value: "demo-hr", label: "demo-hr" },
          ]}
        />
        <SearchField
          label="Busca"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Nome ou tipo"
        />
        <Checkbox
          label="Só atrasadas"
          checked={overdueOnly}
          onChange={(e) => setOverdueOnly(e.target.checked)}
        />
        <Checkbox
          label="Ver aprovadas"
          checked={showClosed}
          onChange={(e) => setShowClosed(e.target.checked)}
        />
      </FilterBar>

      {items.length === 0 ? (
        <EmptyState
          title="Fila vazia"
          description="Nenhuma solicitação com os filtros atuais."
        />
      ) : (
        <Table
          caption="Fila de solicitações"
          getRowId={(item) => item.id}
          columns={[
            { id: "label", header: "Solicitação", cell: (item) => item.label },
            { id: "who", header: "Solicitante", cell: (item) => item.requesterName },
            {
              id: "status",
              header: "Status",
              cell: (item) => (
                <>
                  <Badge tone={badgeToneForStatus(item.status)}>{item.status}</Badge>
                  {item.overdue ? <Badge tone="danger">Atrasada</Badge> : null}
                </>
              ),
            },
            {
              id: "meta",
              header: "Fila",
              priority: "optional",
              cell: (item) =>
                `${item.type}${
                  item.assigneeSubject
                    ? ` · resp. ${item.assigneeSubject}`
                    : " · fila compartilhada"
                }${item.waitingHours != null ? ` · espera ${item.waitingHours}h` : ""}${
                  item.dueAt
                    ? ` · SLA ${new Date(item.dueAt).toLocaleString("pt-BR")}`
                    : ""
                }`,
            },
            {
              id: "actions",
              header: "Ações",
              cell: (item) =>
                item.status === "IN_PROGRESS" ? (
                  <div className="evolua-stack evolua-stack--3">
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busyId === item.id}
                      onClick={() => void onAssign(item.id)}
                    >
                      Assumir
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={busyId === item.id}
                      onClick={() => {
                        setReassignId(item.id);
                        setReassignReason("");
                        setActionError(null);
                      }}
                    >
                      Reatribuir
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={busyId === item.id}
                      onClick={() => {
                        setRespondId(item.id);
                        setRespondComment("");
                        setRespondDecision("CLOSE");
                        setAttachmentId("");
                        setActionError(null);
                      }}
                    >
                      Responder
                    </Button>
                    <Link to={`/pessoas/${item.employeeId}`}>Ver pessoa →</Link>
                  </div>
                ) : (
                  <Link to={`/pessoas/${item.employeeId}`}>Ver pessoa →</Link>
                ),
            },
          ]}
          rows={items}
        />
      )}

      <Modal
        open={Boolean(reassignId)}
        title="Reatribuir responsável"
        onClose={() => setReassignId(null)}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setReassignId(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={!reassignReason.trim() || reassign.isPending}
              onClick={() => void onReassignConfirm()}
            >
              Confirmar
            </Button>
          </>
        }
      >
        <p>O motivo é obrigatório e entra no histórico auditado (`REASSIGNED`).</p>
        <Field
          label="Subject"
          value={reassignTo}
          onChange={(e) => setReassignTo(e.target.value)}
        />
        <Textarea
          label="Motivo da reatribuição"
          value={reassignReason}
          onChange={(e) => setReassignReason(e.target.value)}
          rows={2}
          required
        />
      </Modal>

      <Modal
        open={Boolean(respondId)}
        title="Resposta e encerramento"
        onClose={() => setRespondId(null)}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={() => setRespondId(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              variant={respondDecision === "REJECT" ? "danger" : "primary"}
              disabled={!respondComment.trim() || respond.isPending}
              onClick={() => void onRespondConfirm()}
            >
              Enviar e encerrar
            </Button>
          </>
        }
      >
        <p>
          Comentário obrigatório. Anexo opcional (UUID de objeto já confirmado no
          storage).
        </p>
        <Select
          label="Decisão"
          value={respondDecision}
          onChange={(e) => setRespondDecision(e.target.value as "CLOSE" | "REJECT")}
          options={[
            { value: "CLOSE", label: "Resolver / aprovar" },
            { value: "REJECT", label: "Reprovar" },
          ]}
        />
        <Textarea
          label="Resposta ao solicitante"
          value={respondComment}
          onChange={(e) => setRespondComment(e.target.value)}
          rows={3}
          required
        />
        <Field
          label="Anexo (objectId, opcional)"
          value={attachmentId}
          onChange={(e) => setAttachmentId(e.target.value)}
          placeholder="uuid"
        />
      </Modal>
    </>
  );
}
